import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { engineerService, EngineerJob } from '../../services/engineerService';
import { PageHeader, Badge, FormField, TextInput, Select } from '../../components/ui';

const STATUS_TONE: Record<string, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  PENDING: 'neutral',
  SCHEDULED: 'neutral',
  ASSIGNED: 'info',
  EN_ROUTE: 'warning',
  ON_SITE: 'warning',
  IN_PROGRESS: 'info',
  ON_HOLD: 'warning',
  AWAITING_FOLLOWUP: 'warning',
  COMPLETED: 'success',
  PENDING_VALIDATION: 'info',
  INVOICED: 'success',
  PAID: 'success',
  CANCELLED: 'danger',
};

function formatDate(iso?: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    });
  } catch {
    return '—';
  }
}

function formatTime(iso?: string | null) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

const EngineerJobsList: React.FC = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data = [], isLoading, error } = useQuery({
    queryKey: ['engineer', 'jobs'],
    queryFn: () => engineerService.listJobs({ limit: 200 }),
  });

  const filtered = useMemo(() => {
    let out: EngineerJob[] = data as any;
    if (statusFilter !== 'all') {
      out = out.filter((j) => j.status === statusFilter);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      out = out.filter((j) =>
        [j.title, j.client?.name, j.address, j.postcode]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)),
      );
    }
    return out;
  }, [data, search, statusFilter]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
      <PageHeader
        title="Jobs"
        subtitle="All work assigned to you across recent, active and upcoming dates."
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <FormField label="Search">
          <TextInput
            placeholder="Title, client, address…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </FormField>
        <FormField label="Status">
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="SCHEDULED">Scheduled</option>
            <option value="ASSIGNED">Assigned</option>
            <option value="IN_PROGRESS">In progress</option>
            <option value="AWAITING_FOLLOWUP">Awaiting follow-up</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </Select>
        </FormField>
        <div className="text-xs text-slate-500 self-end pb-2">
          {filtered.length} result{filtered.length === 1 ? '' : 's'}
        </div>
      </div>

      {isLoading && <div className="text-sm text-slate-500">Loading jobs…</div>}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          Couldn't load jobs.
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((j) => (
          <Link
            key={j.id}
            to={`/engineer/jobs/${j.id}`}
            className="block bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:border-blue-300 transition-colors"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs text-slate-500 uppercase tracking-wide">
                  {formatDate(j.scheduledDate)} {formatTime(j.scheduledDate)}
                  {j.jobType ? ` · ${j.jobType}` : ''}
                </div>
                <div className="mt-0.5 text-base font-semibold text-slate-900 truncate">
                  {j.title}
                </div>
                <div className="mt-0.5 text-sm text-slate-600 truncate">
                  {j.client?.name || 'No client'}
                  {j.address ? ` · ${j.address}` : ''}
                  {j.postcode ? ` · ${j.postcode}` : ''}
                </div>
              </div>
              <Badge tone={STATUS_TONE[j.status] || 'neutral'}>
                {j.status.replace(/_/g, ' ')}
              </Badge>
            </div>
          </Link>
        ))}

        {!isLoading && filtered.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
            <div className="text-sm font-semibold text-slate-700">No jobs match your filters</div>
            <div className="text-xs text-slate-500 mt-1">Try clearing search or changing status.</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EngineerJobsList;
